'use client'

import type { MouseEvent, PointerEvent } from 'react'

import ImageComponent from '@/components/UiElements/ImageComponent'

export type SynchronizedZoomState = {
    isZoomed: boolean
    x: number
    y: number
}

type SynchronizedZoomImageProps = {
    src: string
    alt: string
    zoom: SynchronizedZoomState
    onZoomChange: (zoom: SynchronizedZoomState) => void
}

const ZOOM_SCALE = 10 / 3

const getNormalizedPoint = (
    clientX: number,
    clientY: number,
    element: HTMLElement
) => {
    const bounds = element.getBoundingClientRect()
    const x = bounds.width > 0 ? (clientX - bounds.left) / bounds.width : 0.5
    const y = bounds.height > 0 ? (clientY - bounds.top) / bounds.height : 0.5

    return {
        x: Math.min(Math.max(x, 0), 1),
        y: Math.min(Math.max(y, 0), 1),
    }
}

const SynchronizedZoomImage = ({
    src,
    alt,
    zoom,
    onZoomChange,
}: SynchronizedZoomImageProps) => {
    const updateZoomPoint = (event: PointerEvent<HTMLButtonElement>) => {
        if (!zoom.isZoomed) return

        onZoomChange({
            ...zoom,
            ...getNormalizedPoint(event.clientX, event.clientY, event.currentTarget),
        })
    }

    const toggleZoom = (event: MouseEvent<HTMLButtonElement>) => {
        if (zoom.isZoomed) {
            onZoomChange({...zoom, isZoomed: false})
            return
        }

        const point = event.detail === 0
            ? {x: 0.5, y: 0.5}
            : getNormalizedPoint(event.clientX, event.clientY, event.currentTarget)

        onZoomChange({
            isZoomed: true,
            ...point,
        })
    }

    return (
        <button
            type="button"
            className={`relative flex h-[300px] w-[300px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white ${
                zoom.isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
            }`}
            aria-label={`${zoom.isZoomed ? 'Close' : 'Open'} synchronized zoom for ${alt}`}
            onClick={toggleZoom}
            onPointerMove={updateZoomPoint}
            onDragStart={(event) => event.preventDefault()}
        >
            <div
                className="h-full w-full"
                style={{
                    transform: zoom.isZoomed ? `scale(${ZOOM_SCALE})` : 'scale(1)',
                    transformOrigin: `${zoom.x * 100}% ${zoom.y * 100}%`,
                    transition: 'transform 150ms ease-out',
                    willChange: 'transform',
                }}
            >
                <ImageComponent
                    src={src}
                    className="!h-full !w-full !object-contain"
                    style={{width: '100%', height: '100%', objectFit: 'contain'}}
                />
            </div>

            {!zoom.isZoomed && <span className="iiz__btn iiz__hint" aria-hidden="true" />}
        </button>
    )
}

export default SynchronizedZoomImage
